import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations, languages, getTranslation } from '../i18n';

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    // Priority 1: /:lang/ prefix in pathname (canonical for hreflang / AI crawlers)
    if (typeof window !== 'undefined') {
      const seg = window.location.pathname.split('/').filter(Boolean)[0];
      if (seg && translations[seg]) return seg;

      // Priority 2: ?lang=X query param (legacy links) → move to the canonical /{lang}/ URL so the query
      // variant can never be indexed as a duplicate (private pages like /checkin keep their query string).
      const params = new URLSearchParams(window.location.search);
      const urlLang = params.get('lang');
      if (urlLang && translations[urlLang]) {
        const { pathname, hash } = window.location;
        if (!/^\/(admin|checkin|inroom|terrace|redeem|offer|pulse|booking)/.test(pathname)) {
          params.delete('lang');
          const q = params.toString();
          window.history.replaceState(null, '', `${urlLang === 'en' ? (pathname === '/' ? '/' : pathname) : `/${urlLang}${pathname === '/' ? '/' : pathname}`}${q ? `?${q}` : ''}${hash}`);
        }
        return urlLang;
      }
    }

    // Unprefixed URLs are the English edition — the URL is the source of truth for crawlers, renderers and
    // shared links. Returning visitors who chose another language are moved to their /{lang}/ URL instead.
    if (typeof window !== 'undefined') {
      const ua = navigator.userAgent || '';
      const isBot = navigator.webdriver || /bot|crawl|spider|slurp|headless|prerender|lighthouse|facebookexternalhit|whatsapp|telegram|bingpreview|yandex|duckduck|applebot|gptbot|claudebot|perplexity/i.test(ua);
      if (isBot) return 'en';
      const saved = localStorage.getItem('hotel-language');
      const browserLang = (navigator.language || 'en').split('-')[0];
      const preferred = (saved && translations[saved] && saved) || (translations[browserLang] && browserLang) || 'en';
      if (preferred !== 'en' && !window.location.pathname.startsWith('/admin')) {
        const { pathname, search, hash } = window.location;
        window.history.replaceState(null, '', `/${preferred}${pathname === '/' ? '/' : pathname}${search}${hash}`);
        return preferred;
      }
    }
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('hotel-language', lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const t = (key) => getTranslation(lang, key);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, languages }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
